import type {
  ClubSearchItem as ApiClubSearchItem,
  DashboardSearchResponseDataItem,
  DashboardTransactionRef,
  DashboardTeamRef,
  FederationSearchItem as ApiFederationSearchItem,
  LeagueSearchItem as ApiLeagueSearchItem,
  LicenseSearchItem as ApiLicenseSearchItem,
  PlatformSearchItem as ApiPlatformSearchItem,
  TeamSearchItem as ApiTeamSearchItem,
  TournamentRef,
  TournamentSearchItem as ApiTournamentSearchItem,
  TransactionSearchItem as ApiTransactionSearchItem,
  UserSearchItem as ApiUserSearchItem,
} from "@/api/__generated__/schemas";
import { fromApiDateNullable } from "@/graphql";
import type {
  ClubSearchItem,
  GlobalSearchItem,
  LeagueContextSearchItem,
  LeagueContextSearchItemType,
  LeagueSearchItem,
  LicenseSearchItem,
  OrganizationSearchItem,
  PlatformSearchItem,
  TeamSearchItem,
  TeamSearchRef,
  TournamentSearchItem,
  TournamentSearchRef,
  TransactionSearchItem,
  TransactionSearchRef,
  UserSearchItem,
} from "@/layouts/DashboardLayout/GlobalSearch/GlobalSearch.types";
import { mapToClubReference } from "@/mappers/club.mappers";
import { mapToCurrencyCode } from "@/mappers/common.mappers";
import { mapToLeagueReference } from "@/mappers/league.mappers";
import { mapToLicenseReference } from "@/mappers/license.mappers";
import { mapToOrganizationReference } from "@/mappers/organization.mappers";
import { mapToPlatformReference } from "@/mappers/platform.mappers";
import { mapToUserReference } from "@/mappers/user.mappers";
import { PaymentStatus, PaymentStatusValues } from "@/types/payment.types";

const mapToPaymentStatusSafe = (status: string | null): PaymentStatus | null => {
  if (!status) return null;
  const normalized = status.toLowerCase().replace(/_/g, "");
  switch (normalized) {
    case "paid":
    case "completed":
      return "completed";
    case "expired":
      return "expired";
    case "refunded":
      return "refunded";
    case "canceled":
      return "canceled";
    case "pending":
      return "pending";
    case "partiallypaid":
      return "partiallyPaid";
    case "failed":
      return "failed";
    default:
      // Search results can include statuses the FE doesn't recognise yet;
      // surface them as-is when they match the domain enum, otherwise null
      // so views render a neutral fallback.
      return (PaymentStatusValues as readonly string[]).includes(status)
        ? (status as PaymentStatus)
        : null;
  }
};

const mapToTournamentSearchRef = (input: TournamentRef): TournamentSearchRef => ({
  id: input.id,
  title: input.title ?? "",
  logo: input.logo ?? null,
});

const mapToTeamSearchRef = (input: DashboardTeamRef): TeamSearchRef => ({
  id: input.id,
  title: input.title ?? "",
  image: input.image ?? null,
});

const mapToTransactionSearchRef = (input: DashboardTransactionRef): TransactionSearchRef => ({
  id: input.id,
  paymentProcessorIntentId: input.paymentProcessorIntentId ?? null,
  amount: input.amount ?? null,
  currency: input.currency ? mapToCurrencyCode(input.currency) : null,
  paymentStatus: mapToPaymentStatusSafe(input.paymentStatus),
});

const mapToUserSearchItem = (input: ApiUserSearchItem): UserSearchItem => ({
  type: "user",
  id: `user-${input.user.id}`,
  user: mapToUserReference(input.user),
});

const mapToTournamentSearchItem = (input: ApiTournamentSearchItem): TournamentSearchItem => ({
  type: "tournament",
  id: `tournament-${input.tournament.id}`,
  tournament: mapToTournamentSearchRef(input.tournament),
  // Kept defensive until the dashboard client is regenerated from the updated
  // search contract.
  internalId:
    "internalId" in input && typeof input.internalId === "number" ? input.internalId : null,
  startDate: fromApiDateNullable(input.startDate),
  endDate: fromApiDateNullable(input.endDate),
});

const mapToLicenseSearchItem = (input: ApiLicenseSearchItem): LicenseSearchItem => ({
  type: "license",
  id: `license-${input.license.id}`,
  license: mapToLicenseReference(input.license),
  // Kept defensive until the dashboard client is regenerated from the updated
  // search contract (same treatment as the tournament item's `internalId`).
  platformId: readScopeId(
    "platformId" in input ? (input as { platformId?: string | null }).platformId : null
  ),
});

const mapToClubSearchItem = (input: ApiClubSearchItem): ClubSearchItem => ({
  type: "club",
  id: `club-${input.club.id}`,
  club: mapToClubReference(input.club),
});

const mapToLeagueSearchItem = (input: ApiLeagueSearchItem): LeagueSearchItem => ({
  type: "league",
  id: `league-${input.league.id}`,
  league: mapToLeagueReference(input.league),
});

const mapToTeamSearchItem = (input: ApiTeamSearchItem): TeamSearchItem => ({
  type: "team",
  id: `team-${input.team.id}`,
  team: mapToTeamSearchRef(input.team),
});

const mapToTransactionSearchItem = (input: ApiTransactionSearchItem): TransactionSearchItem => ({
  type: "transaction",
  id: `transaction-${input.transaction.id}`,
  transaction: mapToTransactionSearchRef(input.transaction),
});

const mapToPlatformSearchItem = (input: ApiPlatformSearchItem): PlatformSearchItem => ({
  type: "platform",
  id: `platform-${input.platform.id}`,
  platform: mapToPlatformReference(input.platform),
});

const mapToOrganizationSearchItem = (input: ApiFederationSearchItem): OrganizationSearchItem => ({
  type: "organization",
  id: `organization-${input.federation.id}`,
  organization: mapToOrganizationReference(input.federation),
});

const LEAGUE_CONTEXT_TYPES = new Set<LeagueContextSearchItemType>([
  "rankingRule",
  "rankingCategory",
  "tournamentCategory",
  "rankingPointBlock",
  "news",
  "partner",
  "album",
]);

type RawLeagueContextSearchItem = {
  type: LeagueContextSearchItemType;
  id: string;
  title: string | null;
  leagueId: string;
  // Scope pair for the hit's OWN league. Read defensively (like the tournament
  // item's `internalId`) until the dashboard client is regenerated from the
  // updated search contract; a missing pair falls back to the current scope.
  platformId?: string | null;
  federationId?: string | null;
};

/** A scope-pair field, tolerated as absent until codegen catches up. */
const readScopeId = (value: string | null | undefined): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const isLeagueContextSearchItem = (
  input: DashboardSearchResponseDataItem | RawLeagueContextSearchItem
): input is RawLeagueContextSearchItem =>
  LEAGUE_CONTEXT_TYPES.has(input.type as LeagueContextSearchItemType) &&
  "id" in input &&
  typeof input.id === "string" &&
  "title" in input &&
  "leagueId" in input &&
  typeof input.leagueId === "string";

const mapToLeagueContextSearchItem = (
  input: RawLeagueContextSearchItem
): LeagueContextSearchItem => ({
  type: input.type,
  id: input.id,
  title: input.title ?? null,
  leagueId: input.leagueId,
  platformId: readScopeId(input.platformId),
  federationId: readScopeId(input.federationId),
});

export const mapToGlobalSearchItem = (
  item: DashboardSearchResponseDataItem | RawLeagueContextSearchItem
): GlobalSearchItem | null => {
  if (isLeagueContextSearchItem(item)) return mapToLeagueContextSearchItem(item);

  switch (item.type) {
    case "user":
      return mapToUserSearchItem(item);
    case "tournament":
      return mapToTournamentSearchItem(item);
    case "license":
      return mapToLicenseSearchItem(item);
    case "club":
      return mapToClubSearchItem(item);
    case "league":
      return mapToLeagueSearchItem(item);
    case "team":
      return mapToTeamSearchItem(item);
    case "transaction":
      return mapToTransactionSearchItem(item);
    case "platform":
      return mapToPlatformSearchItem(item);
    case "federation":
      return mapToOrganizationSearchItem(item);
    default:
      return null;
  }
};
